import moment from 'moment';
import React from 'react';
import {
  EOrderType,
  SalesOrderItemTable
} from '../../../../store/ducks/new-long-steel-orders/types';
import { EOrderTab } from '../../../../store/ducks/long-steel-orders/types';
import theme from '../../../../assets/styles/theme/theme';
import { formatNumberWithLocale } from '../../../../utils/formatNumbers';
import {
  excelNumberCell,
  IExcelNumberCell,
  isExcelNumberCell
} from '../../../../utils/createXMLTable';

interface OrderTableContentToExcelProps {
  orders: Partial<SalesOrderItemTable>[];
  hasSomeFlatItem: boolean;
  tableType?: EOrderTab;
}

interface ITitleTd {
  title: string;
  width: number;
  backgroundColor: string;
}

interface ITableTr {
  tdsContent: Array<React.Key | IExcelNumberCell>;
}

export const OrderTableContentToExcel: React.FC<
  OrderTableContentToExcelProps
> = ({ orders, hasSomeFlatItem, tableType = EOrderTab.OPEN }) => {
  const evenRowColor = theme.colors.white3;

  const hasCorrida = orders.some(
    (item) =>
      (item?.billing &&
        item?.billing?.length > 0 &&
        item.billing[0]?.productionBatch) ||
      item?.item?.productionBatch ||
      item?.productionBatch
  );

  const formatDateBR = (date: string | undefined): string => {
    if (!date) return ' - ';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(date)) return date;
    const m = moment(
      date.replace('Z', ''),
      ['YYYY-MM-DD', moment.ISO_8601],
      true
    );
    return m.isValid() ? m.format('DD/MM/YYYY') : ' - ';
  };

  const formatBP = (
    code: string | undefined,
    name: string | undefined
  ): string => {
    if (!code && !name) return ' - ';
    if (!code) return name ?? ' - ';
    return `${code} - ${name ?? ' - '}`;
  };

  // Extrai o valor numérico de textos compostos como "1,23 TON"
  const parseCompositeQuantity = (value?: string): IExcelNumberCell | null => {
    const numericValue = value?.match(/[-+]?(?:\d[\d.,]*|[.,]\d+)/)?.[0];
    return excelNumberCell(numericValue);
  };

  const getTitleTds = (): ITitleTd[] => {
    const titleTds: ITitleTd[] = [];

    const addTitleTd = (title: string, width: number) => {
      titleTds.push({ title, width, backgroundColor: '#FFF' });
    };

    if (tableType === EOrderTab.OPEN) {
      addTitleTd('Nº da OV', 96);
      addTitleTd('Id. do pedido', 192);
      addTitleTd('Código de material', 147);
      addTitleTd('Código interno', 140);
      addTitleTd('Descrição do material', 256);
      addTitleTd('Data desejada', 108);
      addTitleTd('Próxima data estimada', 180);
      addTitleTd('BP Emissor', 200);
      addTitleTd('Data de criação', 108);
      addTitleTd('Status da entrega', 220);
      addTitleTd('BP Recebedor', 200);
      addTitleTd('Nº do item', 96);
      addTitleTd('Tipo de produto', 160);
      addTitleTd('Qtd. solicitada', 160);
      addTitleTd('Un. solicitada', 90);
      addTitleTd('Qtd. confirmada (TON)', 160);
      addTitleTd('Saldo (TON)', 160);
      addTitleTd('Em remessa (TON)', 160);
      addTitleTd('Em carregamento (TON)', 160);
      addTitleTd('Faturado (TON)', 160);

      if (hasSomeFlatItem) {
        addTitleTd('Aciaria (TON)', 100);
        addTitleTd('Laminação (TON)', 100);
        addTitleTd('LA (TON)', 100);
        addTitleTd('Acabamento (TON)', 100);
        addTitleTd('Estoque Usina (TON)', 100);
        addTitleTd('Em Transferência (TON)', 100);
        addTitleTd('Estoque Entre-Posto (TON)', 100);
      }

      addTitleTd('Condição de pagamento', 160);
      addTitleTd('Preço unitário c/ impostos', 130);
      addTitleTd('Preço unitário s/ impostos', 130);
      addTitleTd('Preço total c/ impostos', 130);
      addTitleTd('Preço total s/ impostos', 130);

      let totalH = 3;
      orders.forEach((item) => {
        if (item.futureDeliveries && item.futureDeliveries.length > totalH)
          totalH = item.futureDeliveries.length;
      });

      for (let i = 1; i <= totalH; i++) {
        addTitleTd(`Data estimada - ${i}`, 100);
        addTitleTd(`Qtd. Entrega ${i}`, 100);
        addTitleTd(`Un. Entrega ${i}`, 80);
      }
    } else {
      addTitleTd('Nº da nota fiscal', 115);
      addTitleTd('Data do faturamento', 144);
      addTitleTd('Id. do pedido', 192);
      addTitleTd('Código do material', 147);
      addTitleTd('Código interno', 140);
      addTitleTd('Descrição do material', 256);
      addTitleTd('Qtd. faturada', 96);
      addTitleTd('Nº da OV', 96);
      addTitleTd('Data do vencimento', 108);
      addTitleTd('BP Emissor', 200);
      addTitleTd('Nº da fatura', 100);
      addTitleTd('Nº da nota de remessa', 115);
      addTitleTd('Data de criação', 108);
      addTitleTd('BP Recebedor', 200);
      addTitleTd('Nº do item', 96);
      addTitleTd('Condição de pagamento', 160);
      addTitleTd('Preço unitário c/ impostos', 130);
      addTitleTd('Preço unitário s/ impostos', 130);
      addTitleTd('Preço total c/ impostos', 130);
      addTitleTd('Preço total s/ impostos', 130);
      addTitleTd('Total da fatura c/ impostos', 130);
      addTitleTd('Total da fatura s/ impostos', 130);
      addTitleTd('Chave de acesso da NF', 200);

      if (hasCorrida) {
        addTitleTd('Corrida', 128);
      }
    }

    return titleTds;
  };

  const allTitleTds = getTitleTds();

  const getCurrencyValue = (value: string | number | undefined): string => {
    if (
      value === 'Indisponível' ||
      value === undefined ||
      isNaN(Number(value))
    ) {
      return ' - ';
    } else {
      return `R$ ${formatNumberWithLocale({ value: Number(value) })}`;
    }
  };

  const getTableLines = (): ITableTr[] => {
    const trs: ITableTr[] = [];

    orders.forEach((record) => {
      const tdsContent: Array<React.Key | IExcelNumberCell> = [];
      const item = record.item;
      const billing = record.item?.billingHistory;

      if (tableType === EOrderTab.OPEN) {
        // Nº da OV
        tdsContent.push(item?.salesOrder ?? ' - ');
        // Id. do pedido
        tdsContent.push(record.customerOrder ?? ' - ');
        // Código de material
        tdsContent.push(item?.sku ?? ' - ');
        // Código interno
        tdsContent.push(item?.customerSKU ?? ' - ');
        // Descrição do material
        tdsContent.push(item?.materialDescription ?? ' - ');
        // Data desejada
        tdsContent.push(formatDateBR(record.requestedDateTable));
        // Próxima data estimada
        tdsContent.push(
          record.futureDeliveries && record.futureDeliveries.length > 0
            ? `${
                record.futureDeliveries[0]?.estimatedDeliveryQuantity
                  ? parseFloat(
                      record.futureDeliveries[0].estimatedDeliveryQuantity
                    ).toLocaleString('pt-BR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })
                  : ''
              } ${record.nextFutureDeliveryUnit ?? ''} de ${
                record.futureDeliveries[0]?.estimatedDeliveryDate ?? '?'
              }`.trim()
            : ' - '
        );
        // Emissor
        tdsContent.push(formatBP(record.soldTo?.code, record.emitter));
        // Data de criação
        tdsContent.push(formatDateBR(record.creationDateTable));
        // Status da entrega
        tdsContent.push(record.futureDeliveriesTable ?? ' - ');
        // Recebedor
        tdsContent.push(formatBP(record.shipTo?.code, record.receiver));
        // Nº do item
        tdsContent.push(item?.salesOrderItem ?? ' - ');
        // Tipo de produto
        tdsContent.push(item?.divisionDescription || ' - ');
        // Qtd. solicitada (TON)
        tdsContent.push(
          excelNumberCell(item?.quantities?.requestedConversionQuantity) ??
            ' - '
        );
        // Un. solicitada
        tdsContent.push(item?.quantities?.requestedMeasureUnit ?? ' - ');
        // Qtd. confirmada (TON)
        tdsContent.push(
          parseCompositeQuantity(record.totalQuantityConfirmed) ?? ' - '
        );
        // Saldo (TON)
        tdsContent.push(
          excelNumberCell(item?.quantities?.pendingConversionQuantity) ?? ' - '
        );
        // Em remessa (TON)
        tdsContent.push(
          excelNumberCell(item?.quantities?.deliveryConversionQuantity) ?? ' - '
        );
        // Em carregamento (TON)
        tdsContent.push(
          excelNumberCell(item?.quantities?.shipmentConversionQuantity) ?? ' - '
        );
        // Faturado (TON)
        tdsContent.push(
          parseCompositeQuantity(record.totalQuantityBilled) ?? ' - '
        );

        // Aciaria, Laminação, LA, Acabamento, Estoque Usina, Em Transferência, Estoque Entre-Posto
        if (hasSomeFlatItem) {
          const addFlatItem = (
            flatItemValue?: number | string
          ): IExcelNumberCell | string =>
            excelNumberCell(flatItemValue ?? 0) ?? ' - ';
          const isCurrentItemFlatSteel = item?.orderType === EOrderType.PLANOS;
          if (isCurrentItemFlatSteel) {
            const flat =
              record.item?.flatSteelProduction ||
              record.item?.trackingFlatsteelExcel;
            tdsContent.push(
              flat?.meltShop !== undefined ? addFlatItem(flat.meltShop) : ' - '
            );
            tdsContent.push(
              flat?.rollingMill !== undefined
                ? addFlatItem(flat.rollingMill)
                : ' - '
            );
            tdsContent.push(
              flat?.rollingFinish !== undefined
                ? addFlatItem(flat.rollingFinish)
                : ' - '
            );
            tdsContent.push(
              flat?.finish !== undefined ? addFlatItem(flat.finish) : ' - '
            );
            tdsContent.push(
              flat?.stockMill !== undefined
                ? addFlatItem(flat.stockMill)
                : ' - '
            );
            tdsContent.push(
              flat?.transit !== undefined ? addFlatItem(flat.transit) : ' - '
            );
            tdsContent.push(
              flat?.stockWarehouse !== undefined
                ? addFlatItem(flat.stockWarehouse)
                : ' - '
            );
          } else {
            tdsContent.push(' - ', ' - ', ' - ', ' - ', ' - ', ' - ', ' - ');
          }
        }

        // Condição de pagamento
        tdsContent.push(item?.conditions?.paymentTermsDescription || ' - ');

        // Preços
        const isFullyBilledOpen =
          Number(item?.quantities?.pendingConversionQuantity || 0) <= 0 &&
          Number(item?.quantities?.billedConversionQuantity || 0) > 0;
        tdsContent.push(getCurrencyValue(item?.conditions?.unitPriceWithTaxes));
        tdsContent.push(
          getCurrencyValue(item?.conditions?.unitPriceWithoutTaxes)
        );
        tdsContent.push(
          getCurrencyValue(
            isFullyBilledOpen
              ? item?.conditions?.invoiceTotalValueWithTaxes
              : item?.conditions?.totalValueWithTaxes
          )
        );
        tdsContent.push(
          getCurrencyValue(
            isFullyBilledOpen
              ? item?.conditions?.invoiceTotalValueWithoutTaxes
              : item?.conditions?.totalValueWithoutTaxes
          )
        );

        // Datas estimadas
        let totalH = 3;
        orders.forEach((o) => {
          if (o.futureDeliveries && o.futureDeliveries.length > totalH)
            totalH = o.futureDeliveries.length;
        });

        const fd = record.futureDeliveries || [];
        for (let i = 0; i < totalH; i++) {
          if (fd[i]) {
            tdsContent.push(fd[i].estimatedDeliveryDate || ' - ');
            tdsContent.push(
              excelNumberCell(fd[i].estimatedDeliveryQuantity) ?? ' - '
            );
            tdsContent.push(fd[i].estimatedDeliveryMeasureUnit || ' - ');
          } else {
            if (i === 0 && (!fd || fd.length === 0)) {
              tdsContent.push('Pedido não confirmado. Consultar Gerdau.');
              tdsContent.push(' - ');
              tdsContent.push(' - ');
            } else {
              tdsContent.push(' - ');
              tdsContent.push(' - ');
              tdsContent.push(' - ');
            }
          }
        }
      } else {
        // BILLED
        tdsContent.push(record.invoiceNumber || ' - ');

        tdsContent.push(formatDateBR(record.itemBillingDate));

        tdsContent.push(record.customerOrder || ' - ');
        tdsContent.push(billing?.sku || ' - ');
        tdsContent.push(billing?.customerSKU || ' - ');
        tdsContent.push(billing?.materialDescription || ' - ');

        const billedQty = (item?.quantities as any)?.billingQuantity as
          | number
          | undefined;
        tdsContent.push(excelNumberCell(billedQty) ?? ' - ');

        tdsContent.push(billing?.salesOrder || ' - ');

        let parsedDueDates = '-';
        if (record.dueDate && record.dueDate.length > 0) {
          const validDates = record.dueDate
            .filter((d) => moment(d).isValid())
            .map((d) => moment(d, 'YYYY-MM-DD').format('DD/MM/YYYY'));
          if (validDates.length > 0) {
            parsedDueDates = Array.from(new Set(validDates)).join(', ');
          }
        }
        tdsContent.push(parsedDueDates);

        tdsContent.push(formatBP(record.soldTo?.code, record.emitter));
        tdsContent.push(record.billingNumber || ' - ');
        tdsContent.push(record.invoiceZvoi || ' - ');
        tdsContent.push(formatDateBR(record.creationDateTable));
        tdsContent.push(formatBP(record.shipTo?.code, record.receiver));
        tdsContent.push(billing?.salesOrderItem || ' - ');

        // Faturado (TON)
        // tdsContent.push(
        //   item?.quantities?.billedConversionQuantity
        //     ? Number(item.quantities.billedConversionQuantity).toLocaleString(
        //         'pt-BR',
        //         { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        //       )
        //     : ' - '
        // );

        tdsContent.push(
          item?.conditions?.paymentTermsDescription ||
            (record.item?.billing &&
              record.item.billing.length > 0 &&
              record.item.billing[0]?.paymentTermsDescription) ||
            'Indisponível'
        );

        tdsContent.push(getCurrencyValue(item?.conditions?.unitPriceWithTaxes));
        tdsContent.push(
          getCurrencyValue(item?.conditions?.unitPriceWithoutTaxes)
        );
        tdsContent.push(
          getCurrencyValue(item?.conditions?.invoiceTotalValueWithTaxes)
        );
        tdsContent.push(
          getCurrencyValue(item?.conditions?.invoiceTotalValueWithoutTaxes)
        );
        tdsContent.push(
          getCurrencyValue(item?.conditions?.invoiceTotalValueWithTaxes)
        );
        tdsContent.push(
          getCurrencyValue(item?.conditions?.invoiceTotalValueWithoutTaxes)
        );

        const formatNfeKey = (key: string): string =>
          key.replace(/(.{4})(?=.)/g, '$1 ');
        tdsContent.push(
          formatNfeKey(record.nfeAccessKey || item?.nfeAccessKey || '') || ' - '
        );

        if (hasCorrida) {
          const pb = record.productionBatch || item?.productionBatch;
          if (pb) {
            tdsContent.push(pb.split(',').join('\r\n'));
          } else {
            tdsContent.push('-');
          }
        }
      }

      trs.push({ tdsContent });
    });

    return trs;
  };

  const allTrs = getTableLines();

  return (
    <tbody>
      <tr
        style={{
          fontSize: '14px',
          verticalAlign: 'middle',
          whiteSpace: 'pre-wrap'
        }}
      >
        {allTitleTds.map((td) => (
          <td
            key={td.title}
            align="center"
            style={{
              height: `80px`,
              width: `${td.width}px`,
              background: td.backgroundColor
            }}
          >
            {td.title}
          </td>
        ))}
      </tr>

      {allTrs.map((trs, idx) => (
        <tr key={idx}>
          {trs.tdsContent.map((td, tIndex) => {
            const numberCell = isExcelNumberCell(td) ? td : null;
            return (
              <td
                key={`${idx}-${tIndex}`}
                data-excel-number={numberCell?.value}
                style={{
                  whiteSpace: 'nowrap',
                  fontSize: '14px',
                  background: idx & 1 ? evenRowColor : 'white'
                }}
              >
                {numberCell ? numberCell.value : td}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};
